import { t } from "i18next";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { DeviceGattApi } from "./DeviceGattApi";
import { showNotification } from "./notification";
import DeviceDataDisplay from "./DeviceDataDisplay";
import { Col, Container, Row, Stack, Tab, Tabs } from "react-bootstrap";
import DeviceModeButton from "./DeviceModeButton";
import DeviceConfig from "./DeviceConfig";
import DeviceLog from "./DeviceLog";

function DeviceDisplay({ deviceL, deviceR, batteryStatusChangedL, batteryStatusChangedR }) {
    const deviceNameL = useMemo(() => deviceL ? (deviceL?.name || t("device.unknown")) : t("device.notConnected"), [deviceL]);
    const deviceNameR = useMemo(() => deviceR ? (deviceR?.name || t("device.unknown")) : t("device.notConnected"), [deviceR]);

    const deviceIdL = useMemo(() => deviceL ? (deviceL?.id || t("device.unknown")) : t("device.notConnected"), [deviceL]);
    const deviceIdR = useMemo(() => deviceR ? (deviceR?.id || t("device.unknown")) : t("device.notConnected"), [deviceR]);

    const deviceGattApiL = useMemo(() => deviceL && new DeviceGattApi(deviceL), [deviceL]);
    const deviceGattApiR = useMemo(() => deviceR && new DeviceGattApi(deviceR), [deviceR]);

    const [deviceDataL, setDeviceDataL] = useState(null);
    const [deviceDataR, setDeviceDataR] = useState(null);

    let updateInProcess = useRef(false);
    let mode = useRef(null);
    let configuration = useRef(null);

    let readLogL = useRef(null);
    let readLogR = useRef(null);
    let clearLogL = useRef(null);
    let clearLogR = useRef(null);

    const [currentChunk, setCurrentChunk] = useState(null);
    const [totalChunks, setTotalChunks] = useState(null);

    const [isLogReadingL, setIsLogReadingL] = useState(false);
    const [isLogReadingR, setIsLogReadingR] = useState(false);
    const [reportsL, setReportsL] = useState(null);
    const [reportsR, setReportsR] = useState(null);

    function changeMode(newMode) {
        mode.current = newMode;
    }

    function changeConfiguration(newConfiguration) {
        configuration.current = newConfiguration;
    }

    useEffect(() => {
        function updateBatteryStatus(deviceData, deviceKind) {
            let batteryLevel = 0;
            if (deviceData && DeviceGattApi.isCharging(deviceData)) {
                batteryLevel = -1;
            } else if (deviceData && deviceData.deviceInfo?.batteryLevel) {
                batteryLevel = deviceData.deviceInfo?.batteryLevel;
            } else {
                batteryLevel = 0;
            }

            if (deviceKind === "left") {
                batteryStatusChangedL(batteryLevel);
            } else if (deviceKind === "right") {
                batteryStatusChangedR(batteryLevel);
            }
        }

        if (deviceDataL) {
            updateBatteryStatus(deviceDataL, "left");
        }
        if (deviceDataR) {
            updateBatteryStatus(deviceDataR, "right");
        }
    }, [deviceDataL, deviceDataR, batteryStatusChangedL, batteryStatusChangedR]);

    useLayoutEffect(() => {
        const updateData = async () => {
            // check that update is not in progress
            if (updateInProcess.current) {
                return;
            }

            updateInProcess.current = true;

            // update data
            if (deviceGattApiL || deviceGattApiR) {
                try {
                    // change mode if needed
                    if (mode.current !== null) {
                        await Promise.all([
                            deviceGattApiL && deviceGattApiL.changeControlPoint(mode.current),
                            deviceGattApiR && deviceGattApiR.changeControlPoint(mode.current)
                        ]);
                        mode.current = null;

                        showNotification(t("device.modeChanged"), "success");
                    }

                    // change configuration if needed
                    if (configuration.current !== null) {
                        await Promise.all([
                            deviceGattApiL && deviceGattApiL.changeObjectProperties(configuration.current),
                            deviceGattApiR && deviceGattApiR.changeObjectProperties(configuration.current)
                        ]);
                        configuration.current = null;

                        showNotification(t("device.configurationChanged"), "success");
                    }

                    // read log
                    if (readLogL.current && deviceGattApiL) {
                        setIsLogReadingL(true);
                        const reports = await deviceGattApiL.readReports((chunk, total) => {
                            setCurrentChunk(chunk);
                            setTotalChunks(total);
                        });

                        setReportsL(reports);
                        readLogL.current = false;
                        setIsLogReadingL(false);
                    }

                    if (readLogR.current && deviceGattApiR) {
                        setIsLogReadingR(true);
                        const reports = await deviceGattApiR.readReports((chunk, total) => {
                            setCurrentChunk(chunk);
                            setTotalChunks(total);
                        });

                        setReportsR(reports);
                        readLogR.current = false;
                        setIsLogReadingR(false);
                    }

                    // execute log clear command
                    if (clearLogL.current && deviceGattApiL) {
                        await deviceGattApiL.clearReports();
                        clearLogL.current = false;

                        showNotification(t("device.logCleared"), "success");
                    }

                    if (clearLogR.current && deviceGattApiR) {
                        await deviceGattApiR.clearReports();
                        clearLogR.current = false;

                        showNotification(t("device.logCleared"), "success");
                    }

                    // update all data
                    const updateData = async (deviceGattApi, setDeviceData) => {
                        if (deviceGattApi) {
                            const updatedData = await deviceGattApi.updateAllData();
                            setDeviceData(updatedData);
                        } else {
                            setDeviceData(null);
                        }
                    };
                    await Promise.all([
                        updateData(deviceGattApiL, setDeviceDataL),
                        updateData(deviceGattApiR, setDeviceDataR)
                    ]);
                } catch (error) {
                    console.error(error);
                    showNotification(t("device.gattError") + ": " + error.message, "danger");
                }
            }

            updateInProcess.current = false;
        };

        const interval = setInterval(updateData, 1000);
        updateData();

        return () => clearInterval(interval);
    }, [deviceGattApiL, deviceGattApiR, mode, configuration]);

    return (
        <div>
            <h3 className="mt-3">{deviceNameL === deviceNameR ? deviceNameL : deviceNameL + " / " + deviceNameR}</h3>

            <p className="text-muted mt-2"><Trans>device.id</Trans>: {deviceIdL} / {deviceIdR}</p>

            <h5 className="mt-3"><Trans>device.mode.title</Trans></h5>
            <Stack direction="horizontal" gap={1}>
                <DeviceModeButton
                    data={deviceDataL || deviceDataR}
                    mode={DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_SMART_HEAT}
                    text={t("device.mode.auto")}
                    onClick={changeMode} />
                <DeviceModeButton
                    data={deviceDataL || deviceDataR}
                    mode={DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_100}
                    text={t("device.mode.max")}
                    onClick={changeMode} />
                <DeviceModeButton
                    data={deviceDataL || deviceDataR}
                    mode={DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_50}
                    text={t("device.mode.med")}
                    onClick={changeMode} />
                <DeviceModeButton
                    data={deviceDataL || deviceDataR}
                    mode={DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_25}
                    text={t("device.mode.min")}
                    onClick={changeMode} />
                <DeviceModeButton
                    data={deviceDataL || deviceDataR}
                    mode={DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_PWM_0}
                    text={t("device.mode.off")}
                    onClick={changeMode} />
                <DeviceModeButton
                    data={deviceDataL || deviceDataR}
                    mode={DeviceGattApi.FITNESS_MACHINE_CONTROL_POINT_SHUTDOWN}
                    text={t("device.mode.shutdown")}
                    onClick={changeMode}
                    danger />
            </Stack>

            {(deviceDataL || deviceDataR) && (
                <Tabs defaultActiveKey="status" id="device-tabs" className="mt-3">
                    <Tab eventKey="status" title={t("device.status.tab")}>
                        <div className="mt-2">
                            <DeviceDataDisplay dataL={deviceDataL} dataR={deviceDataR} />
                        </div>
                    </Tab>

                    <Tab eventKey="config" title={t("device.config.tab")}>
                        <div className="mt-2">
                            <DeviceConfig dataL={deviceDataL} dataR={deviceDataR} handleChange={changeConfiguration} />
                        </div>
                    </Tab>

                    <Tab eventKey="log" title={t("device.log.tab")}>
                        <Container className="mt-2">
                            <Row>
                                <Col xs={12} lg={6}>
                                    {deviceGattApiL && (
                                        <DeviceLog
                                            reports={reportsL}
                                            isReading={isLogReadingL}
                                            currentChunk={currentChunk}
                                            totalChunks={totalChunks}
                                            handleRead={() => readLogL.current = true}
                                            handleClear={() => clearLogL.current = true}
                                            data={deviceDataL} />
                                    )}
                                </Col>

                                <Col xs={12} lg={6}>
                                    {deviceGattApiR && (
                                        <DeviceLog
                                            reports={reportsR}
                                            isReading={isLogReadingR}
                                            currentChunk={currentChunk}
                                            totalChunks={totalChunks}
                                            handleRead={() => readLogR.current = true}
                                            handleClear={() => clearLogR.current = true}
                                            data={deviceDataR} />
                                    )}
                                </Col>
                            </Row>
                        </Container>
                    </Tab>
                </Tabs>
            )}
        </div>
    );
}

export default DeviceDisplay;
